
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COLORS } from './demographics/constants';
import { DemographicData } from './demographics/types';
import DemographicLoadingState from './demographics/LoadingState';
import DemographicEmptyState from './demographics/EmptyState';
import DemographicTab from './demographics/DemographicTab';
import { useIsMobile } from '@/hooks/use-mobile';

interface PollDemographicAnalysisProps {
  demographicData: DemographicData[];
  isLoading: boolean;
}

const PollDemographicAnalysis = ({ demographicData, isLoading }: PollDemographicAnalysisProps) => {
  const [activeTab, setActiveTab] = useState<string>('');
  const isMobile = useIsMobile();
  
  // Find the "Age Group" category or fall back to the first category
  useEffect(() => {
    if (demographicData.length > 0) {
      const ageGroupCategory = demographicData.find(data => 
        data.category === 'Age Group'
      );
      setActiveTab(ageGroupCategory ? ageGroupCategory.category : demographicData[0].category);
    }
  }, [demographicData]);

  if (isLoading) {
    return <DemographicLoadingState />;
  }

  if (!demographicData || demographicData.length === 0) {
    return <DemographicEmptyState />;
  }

  return (
    <Card className="w-full overflow-x-hidden">
      <CardHeader className="pb-4 overflow-x-hidden">
        <CardTitle className="text-lg sm:text-xl">Demographic Analysis</CardTitle>
      </CardHeader>
      <CardContent className="w-full overflow-x-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {isMobile ? (
            // Mobile: Use dropdown selector
            <div className="mb-4 w-full">
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger className="w-full">
                  <SelectValue>
                    {activeTab || "Select demographic category"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {demographicData.map((data) => (
                    <SelectItem key={data.category} value={data.category}>
                      {data.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            // Desktop/Tablet: Use tabs as before
            <TabsList className="mb-4 flex flex-wrap w-full overflow-x-auto scrollbar-hide">
              {demographicData.map((data) => (
                <TabsTrigger key={data.category} value={data.category} className="whitespace-nowrap flex-shrink-0">
                  {data.category}
                </TabsTrigger>
              ))}
            </TabsList>
          )}

          {demographicData.map((data) => (
            <TabsContent key={data.category} value={data.category} className="w-full overflow-x-hidden">
              <DemographicTab 
                data={data}
                colors={COLORS}
              />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default PollDemographicAnalysis;
